import { InitialFluidContactType_api } from "@api";
import { defaultContinuousDivergingColorPalettes, defaultContinuousSequentialColorPalettes } from "@framework/utils/colorPalettes";
import { ColorScale, ColorScaleGradientType, ColorScaleType } from "@lib/utils/ColorScale";

import { BooleanNumberSetting } from "../implementations/BooleanNumberSetting";
import { BooleanSetting } from "../implementations/BooleanSetting";
import { ColorScaleSetting } from "../implementations/ColorScaleSetting";
import { ColorSetSetting } from "../implementations/ColorSetSetting";
import { DrilledWellborePicksSetting } from "../implementations/DrilledWellborePicksSetting";
import { DrilledWellboresSetting } from "../implementations/DrilledWellboresSetting";
import { DropdownNumberSetting } from "../implementations/DropdownNumberSetting";
import { DropdownStringSetting } from "../implementations/DropdownStringSetting";
import { EnsembleSetting } from "../implementations/EnsembleSetting";
import { FlowFilterSetting } from "../implementations/FlowFilterSetting";
import { GridLayerRangeSetting } from "../implementations/GridLayerRangeSetting";
import { InputNumberSetting } from "../implementations/InputNumberSetting";
import { IntersectionSetting } from "../implementations/IntersectionSetting";
import { LogCurveSetting } from "../implementations/LogCurveSetting";
import { NumberRangeDropdownSetting } from "../implementations/NumberRangeDropdownSetting";
import { PolygonVisualizationSetting } from "../implementations/PolygonVisualizationSetting";
import { RadioGroupSetting } from "../implementations/RadioGroupSetting";
import { RepresentationSetting } from "../implementations/RepresentationSetting";
import { SeismicSliceSetting } from "../implementations/SeismicSliceSetting";
import { SelectNumberSetting } from "../implementations/SelectNumberSetting";
import { SelectStringSetting } from "../implementations/SelectStringSetting";
import { SensitivitySetting } from "../implementations/SensitivitySetting";
import { SingleColorSetting } from "../implementations/SingleColorSetting";
import { SliderNumberSetting } from "../implementations/SliderNumberSetting";
import { SliderRangeSetting } from "../implementations/SliderRangeSetting";
import { StaticRotationSetting } from "../implementations/StaticRotationSetting";
import { StatisticFunctionSetting } from "../implementations/StatisticFunctionSetting";
import { SurfaceAttributeSetting } from "../implementations/SurfaceAttributeSetting";
import { TimeOrIntervalSetting } from "../implementations/TimeOrIntervalSetting";
import { TimePointPairSetting } from "../implementations/TimePointPairSetting";
import { TimeTypeSetting } from "../implementations/TimeTypeSetting";
import { WellboreDepthFilterAttributeSetting } from "../implementations/WellboreDepthFilterAttributeSetting";
import { WellboreDepthFilterSetting } from "../implementations/WellboreDepthFilterSetting";
import { Setting } from "../settingsDefinitions";

import { SettingRegistry } from "./_SettingRegistry";

const FLUID_CONTACT_LABELS: Record<InitialFluidContactType_api, string> = {
    [InitialFluidContactType_api.FGL]: "Free gas level",
    [InitialFluidContactType_api.FWL]: "Free water level",
    [InitialFluidContactType_api.GOC]: "Gas-oil contact",
    [InitialFluidContactType_api.GWC]: "Gas-water contact",
    [InitialFluidContactType_api.OWC]: "Oil-water contact",
};

SettingRegistry.registerSetting(Setting.TRACK_WIDTH, "Track width", InputNumberSetting, {
    customConstructorParameters: [{ min: 1, max: 6 }],
});

// @ts-expect-error -- Setting type is a string literal, but Dropdown setting doesn't accept that as a valid type
SettingRegistry.registerSetting(Setting.SCALE, "Scale", DropdownStringSetting, {
    customConstructorParameters: [
        {
            options: [
                { value: "log", label: "Logarithmic" },
                { value: "linear", label: "Linear" },
            ],
        },
    ],
});

// @ts-expect-error -- Setting type is a string literal, but Dropdown setting doesn't accept that as a valid type
SettingRegistry.registerSetting(Setting.PLOT_VARIANT, "Plot variant", DropdownStringSetting);
SettingRegistry.registerSetting(Setting.LOG_CURVE, "Log curve", LogCurveSetting);
SettingRegistry.registerSetting(Setting.SHOW_LABELS, "Show labels", BooleanSetting);
SettingRegistry.registerSetting(Setting.LABEL_ROTATION, "Label rotation", StaticRotationSetting);
SettingRegistry.registerSetting(Setting.SHOW_LINES, "Show lines", BooleanSetting);

SettingRegistry.registerSetting(Setting.ATTRIBUTE, "Attribute", DropdownStringSetting);
SettingRegistry.registerSetting(Setting.SURFACE_ATTRIBUTE, "Surface Attribute", SurfaceAttributeSetting);
SettingRegistry.registerSetting(Setting.SEISMIC_ATTRIBUTE, "Seismic Attribute", SurfaceAttributeSetting);
SettingRegistry.registerSetting(Setting.DEPTH_ATTRIBUTE, "Depth Attribute", SurfaceAttributeSetting);
SettingRegistry.registerSetting(Setting.ENSEMBLE, "Ensemble", EnsembleSetting);
SettingRegistry.registerSetting(Setting.COLOR_SCALE, "Color Scale", ColorScaleSetting);
SettingRegistry.registerSetting(Setting.DEPTH_COLOR_SCALE, "Depth Color Scale", ColorScaleSetting, {
    customConstructorParameters: [
        {
            initialColorScale: {
                areBoundariesUserDefined: false,
                colorScale: new ColorScale({
                    colorPalette: defaultContinuousSequentialColorPalettes[0],
                    gradientType: ColorScaleGradientType.Sequential,
                    type: ColorScaleType.Continuous,
                    steps: 10,
                }),
            },
        },
    ],
});
SettingRegistry.registerSetting(Setting.SEISMIC_COLOR_SCALE, "Seismic Color Scale", ColorScaleSetting, {
    customConstructorParameters: [
        {
            initialColorScale: {
                areBoundariesUserDefined: false,
                colorScale: new ColorScale({
                    colorPalette: defaultContinuousDivergingColorPalettes[0],
                    gradientType: ColorScaleGradientType.Diverging,
                    type: ColorScaleType.Continuous,
                    steps: 10,
                }),
            },
        },
    ],
});
SettingRegistry.registerSetting(Setting.COLOR, "Color", SingleColorSetting);
SettingRegistry.registerSetting(Setting.COLOR_SET, "Color Set", ColorSetSetting);
SettingRegistry.registerSetting(Setting.CONTOURS, "Contours", BooleanNumberSetting, {
    customConstructorParameters: [{ min: 10, max: 200 }],
});
SettingRegistry.registerSetting(Setting.GRID_LAYER_K, "Grid Layer K", NumberRangeDropdownSetting);
SettingRegistry.registerSetting(Setting.GRID_LAYER_RANGE, "Grid Ranges", GridLayerRangeSetting);
SettingRegistry.registerSetting(Setting.GRID_NAME, "Grid Name", DropdownStringSetting);
SettingRegistry.registerSetting(Setting.FLUID_CONTACT, "Fluid Contact", DropdownStringSetting, {
    customConstructorParameters: [
        {
            valueLabel: (value) => FLUID_CONTACT_LABELS[value as InitialFluidContactType_api] ?? value,
        },
    ],
});
SettingRegistry.registerSetting(Setting.INTERSECTION, "Intersection", IntersectionSetting, {
    customConstructorParameters: [{ extensionLengthConfig: { min: 0, max: 5000, defaultValue: 500 } }],
});
SettingRegistry.registerSetting(Setting.OPACITY_PERCENT, "Color Opacity", SliderNumberSetting, {
    customConstructorParameters: [{ minMax: { min: 0, max: 100 }, step: 1 }],
});
SettingRegistry.registerSetting(Setting.POLYGONS_ATTRIBUTE, "Polygons Attribute", DropdownStringSetting);
SettingRegistry.registerSetting(Setting.POLYGONS_NAME, "Polygons Name", DropdownStringSetting);
SettingRegistry.registerSetting(Setting.POLYGON_VISUALIZATION, "Polygon Visualization", PolygonVisualizationSetting);
SettingRegistry.registerSetting(Setting.REALIZATION, "Realization", DropdownNumberSetting);
SettingRegistry.registerSetting(Setting.REALIZATIONS, "Realizations", SelectNumberSetting);
SettingRegistry.registerSetting(Setting.SEISMIC_SLICES, "Seismic Slices", SeismicSliceSetting);
SettingRegistry.registerSetting(Setting.SENSITIVITY, "Sensitivity", SensitivitySetting);
SettingRegistry.registerSetting(Setting.SHOW_GRID_LINES, "Show Grid Lines", BooleanSetting);
SettingRegistry.registerSetting(Setting.SMDA_INTERPRETER, "SMDA Interpreter", DropdownStringSetting);
SettingRegistry.registerSetting(Setting.WELLBORES, "Wellbores", DrilledWellboresSetting);
SettingRegistry.registerSetting(Setting.STATISTIC_FUNCTION, "Statistic Function", StatisticFunctionSetting);
SettingRegistry.registerSetting(Setting.STRAT_COLUMN, "Stratigraphic Column", DropdownStringSetting);
SettingRegistry.registerSetting(Setting.SURFACE_NAME, "Surface Name", DropdownStringSetting, {
    customConstructorParameters: [{ showBrowseButtons: true }],
});
SettingRegistry.registerSetting(Setting.FORMATION_NAME, "Formation Name", DropdownStringSetting, {
    customConstructorParameters: [{ showBrowseButtons: true }],
});
SettingRegistry.registerSetting(Setting.SURFACE_NAMES, "Surface Names", SelectStringSetting);
SettingRegistry.registerSetting(Setting.TIME_TYPE, "Time Type", TimeTypeSetting);
SettingRegistry.registerSetting(Setting.TIME_OR_INTERVAL, "Time or Interval", TimeOrIntervalSetting);
SettingRegistry.registerSetting(Setting.TIME_POINT, "Time Point", TimeOrIntervalSetting);
SettingRegistry.registerSetting(Setting.TIME_INTERVAL, "Time Interval", TimeOrIntervalSetting);
SettingRegistry.registerSetting(Setting.TIME_POINT_PAIR, "Time Steps", TimePointPairSetting);

SettingRegistry.registerSetting(Setting.WELLBORE_PICKS, "Wellbore Picks", DrilledWellborePicksSetting);
SettingRegistry.registerSetting(Setting.REPRESENTATION, "Representation", RepresentationSetting);
SettingRegistry.registerSetting(Setting.WELLBORE_DEPTH_FILTER_TYPE, "Depth Filter", RadioGroupSetting, {
    customConstructorParameters: [
        {
            staticOptions: [
                { value: "none", label: "None" },
                { value: "md_range", label: "MD" },
                { value: "surface_based", label: "Surface" },
            ],
            layout: "horizontal",
        },
    ],
});
SettingRegistry.registerSetting(Setting.MD_RANGE, "MD Range", SliderRangeSetting);
SettingRegistry.registerSetting(Setting.TVD_RANGE, "TVD Range", SliderRangeSetting);
SettingRegistry.registerSetting(
    Setting.WELLBORE_DEPTH_FILTER_ATTRIBUTE,
    "Surface Attribute",
    WellboreDepthFilterAttributeSetting,
);
SettingRegistry.registerSetting(
    Setting.WELLBORE_DEPTH_FORMATION_FILTER,
    "Formation Filter",
    WellboreDepthFilterSetting,
);
SettingRegistry.registerSetting(Setting.FLOW_FILTER, "Flow Data Cut-off", FlowFilterSetting);
SettingRegistry.registerSetting(Setting.FLOW_FILTER_TYPE, "Flow Data Filter", RadioGroupSetting, {
    customConstructorParameters: [
        {
            staticOptions: [
                { value: "none", label: "None" },
                { value: "production_injection", label: "Production/Injection" },
            ],
            layout: "horizontal",
        },
    ],
});
